// Page scaffolding: Page (width + padding), PageHeader, PageColumns, StickySaveBar.
import { useEffect, useState, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { Link, useNavigate } from 'react-router'
import { ArrowLeft, ChevronRight, Ellipsis } from 'lucide-react'
import { cn } from './cn'
import { Button } from './button'
import { DropdownMenu, MenuItem, MenuSeparator } from './menu'
import { Kbd } from './kbd'
import { confirm } from './dialog'
import { isModalOpen, useHotkeys, useUnsavedChanges, usePageTitle } from '@/lib/hooks'
import { blockOnInvalidDraft } from './draft'

const widths = { narrow: 'max-w-[760px]', default: 'max-w-[1040px]', wide: 'max-w-[1320px]', full: 'max-w-none' } as const

/**
 * Page container: consistent padding, max width and vertical rhythm.
 * `<Page width="default">` for detail pages, "wide" for lists, "narrow" for settings forms.
 */
export function Page({ width = 'default', className, children }: { width?: keyof typeof widths; className?: string; children: ReactNode }) {
  return <div className={cn('mx-auto flex w-full flex-col gap-5 px-4 pt-5 pb-24 sm:px-6 lg:px-8 lg:pt-7', widths[width], className)}>{children}</div>
}

/** Two-column detail layout: main (2/3) + sidebar (1/3), stacking below 1024 px. */
export function PageColumns({ main, aside, className }: { main: ReactNode; aside: ReactNode; className?: string }) {
  return (
    <div className={cn('grid items-start gap-5 lg:grid-cols-[minmax(0,1fr)_minmax(0,340px)]', className)}>
      <div className="flex min-w-0 flex-col gap-5">{main}</div>
      <div className="flex min-w-0 flex-col gap-5">{aside}</div>
    </div>
  )
}

export interface PageAction {
  label: string
  icon?: ReactNode
  onSelect?: () => void
  /** Navigate instead of onSelect. */
  to?: string
  destructive?: boolean
  disabled?: boolean
  loading?: boolean
  shortcut?: string
}

export interface PageHeaderProps {
  title: ReactNode
  /** Back link (detail pages): `{ to: '/orders', label: 'Orders' }`. */
  back?: { to: string; label?: string }
  /** Breadcrumb trail instead of a back arrow. The last crumb is the current page's parent. */
  breadcrumbs?: Array<{ label: ReactNode; to?: string }>
  /** Badges next to the title (<StatusBadge/>). */
  status?: ReactNode
  /** One line under the title (dates, counts, source). */
  subtitle?: ReactNode
  /** Main call to action (a primary <Button/>). */
  primaryAction?: ReactNode
  /** Secondary actions: buttons on desktop, folded into "More" below 640 px. */
  secondaryActions?: PageAction[]
  /** Always in the "More actions" menu (destructive ones last). */
  moreActions?: PageAction[]
  /** Sets document.title (default: the title when it is a string). Pass null to leave the route title. */
  documentTitle?: string | null
  className?: string
}

/**
 * Page title row: back/breadcrumbs, title + status, subtitle, actions.
 * `<PageHeader title="Products" primaryAction={<Button variant="primary" asChild><Link to="/products/new">Add product</Link></Button>}
 *   secondaryActions={[{ label:'Export', icon:<Download/>, onSelect: exportCsv }]} />`
 */
export function PageHeader({ title, back, breadcrumbs, status, subtitle, primaryAction, secondaryActions = [], moreActions = [], documentTitle, className }: PageHeaderProps) {
  usePageTitle(documentTitle === undefined ? (typeof title === 'string' ? title : null) : documentTitle)
  const navigate = useNavigate()
  const renderAction = (a: PageAction, i: number) => a.to
    ? <Button key={i} asChild variant={a.destructive ? 'danger-ghost' : 'secondary'} icon={a.icon} disabled={a.disabled}><Link to={a.to}>{a.icon}{a.label}</Link></Button>
    : <Button key={i} variant={a.destructive ? 'danger-ghost' : 'secondary'} icon={a.icon} onClick={a.onSelect} disabled={a.disabled} loading={a.loading} shortcut={a.shortcut}>{a.label}</Button>
  const menuItems = (list: PageAction[]) => list.map((a, i) => (
    <MenuItem key={i} icon={a.icon} destructive={a.destructive} disabled={a.disabled} shortcut={a.shortcut}
      onSelect={() => { if (a.to) navigate(a.to); a.onSelect?.() }}>
      {a.label}
    </MenuItem>
  ))
  const nonDestructiveMore = moreActions.filter(a => !a.destructive)
  const destructiveMore = moreActions.filter(a => a.destructive)
  return (
    <header className={cn('flex flex-col gap-3', className)}>
      {breadcrumbs && breadcrumbs.length > 0 && (
        <nav aria-label="Breadcrumb" className="-mb-1 flex min-w-0 items-center gap-1 text-sm text-text-muted">
          {breadcrumbs.map((c, i) => (
            <span key={i} className="flex min-w-0 items-center gap-1">
              {i > 0 && <ChevronRight className="size-3.5 shrink-0 text-text-subtle" aria-hidden />}
              {c.to ? <Link to={c.to} className="truncate rounded hover:text-text focus-visible:outline-2 focus-visible:outline-focus">{c.label}</Link> : <span className="truncate">{c.label}</span>}
            </span>
          ))}
        </nav>
      )}
      <div className="flex flex-wrap items-start justify-between gap-x-4 gap-y-3">
        <div className="flex min-w-0 items-start gap-2">
          {back && (
            <Button asChild variant="ghost" size="md" className="-ml-2 mt-px w-8 shrink-0 px-0" aria-label={back.label ? `Back to ${back.label}` : 'Back'} tooltip={back.label ? `Back to ${back.label}` : 'Back'}>
              <Link to={back.to}><ArrowLeft className="size-4" /></Link>
            </Button>
          )}
          <div className="flex min-w-0 flex-col gap-1">
            <div className="flex min-w-0 flex-wrap items-center gap-x-3 gap-y-1">
              <h1 className="min-w-0 truncate text-xl font-semibold tracking-[-0.012em] text-text">{title}</h1>
              {status && <div className="flex flex-wrap items-center gap-2">{status}</div>}
            </div>
            {subtitle && <p className="text-base text-text-muted">{subtitle}</p>}
          </div>
        </div>
        {(primaryAction || secondaryActions.length > 0 || moreActions.length > 0) && (
          <div className="ml-auto flex shrink-0 items-center gap-2">
            {secondaryActions.length > 0 && <div className="hidden items-center gap-2 sm:flex">{secondaryActions.map(renderAction)}</div>}
            {(moreActions.length > 0 || secondaryActions.length > 0) && (
              <div className={cn(moreActions.length === 0 && 'sm:hidden')}>
                <DropdownMenu trigger={<Button icon={<Ellipsis />} tooltip="More actions" aria-label="More actions" />}>
                  <div className="sm:hidden">
                    {menuItems(secondaryActions)}
                    {moreActions.length > 0 && secondaryActions.length > 0 && <MenuSeparator />}
                  </div>
                  {menuItems(nonDestructiveMore)}
                  {destructiveMore.length > 0 && nonDestructiveMore.length > 0 && <MenuSeparator />}
                  {menuItems(destructiveMore)}
                </DropdownMenu>
              </div>
            )}
            {primaryAction}
          </div>
        )}
      </div>
    </header>
  )
}

export interface StickySaveBarProps {
  /** Show the bar (form has unsaved changes). */
  dirty: boolean
  onSave: () => void
  /** Reset the form. Asks for confirmation first unless `confirmDiscard` is false. */
  onDiscard: () => void
  saving?: boolean
  /** Disable Save (e.g. client validation failing) — Discard stays available. */
  saveDisabled?: boolean
  message?: ReactNode
  saveLabel?: string
  confirmDiscard?: boolean
}

/**
 * Contextual save bar: slides in when the form is dirty, ⌘S / Ctrl+S saves, and leaving the page
 * (links, back, reload, ⌘K) asks first. `<StickySaveBar dirty={form.dirty} saving={form.submitting} onSave={form.submit} onDiscard={() => form.reset()} />`
 * While it's mounted ⌘S never opens the browser's "Save page as" — not even inside a popover. It
 * saves unless a modal (dialog, sheet, confirm) is open, and refuses while a control shows an
 * invalid draft (MoneyInput "1.2.3"): the field is focused and the reason toasted.
 */
export function StickySaveBar({ dirty, onSave, onDiscard, saving, saveDisabled, message = 'Unsaved changes', saveLabel = 'Save', confirmDiscard = true }: StickySaveBarProps) {
  useUnsavedChanges(dirty)
  const save = () => {
    if (saving || saveDisabled || blockOnInvalidDraft()) return
    onSave()
  }
  useHotkeys({
    'mod+s': () => {
      // A modal on top owns the keyboard: let a binding it registered have ⌘S (default already prevented).
      if (isModalOpen()) return false
      if (dirty) save()
    },
  }, { inInputs: true, inModals: true, enabled: true })   // handlers are read through a ref: no re-registration per render
  const [mounted, setMounted] = useState(false)
  useEffect(() => setMounted(true), [])
  if (!mounted || !dirty) return null
  const discard = async () => {
    if (!confirmDiscard || await confirm({ title: 'Discard all unsaved changes?', description: 'Your edits will be lost. This can’t be undone.', confirmLabel: 'Discard', destructive: true })) onDiscard()
  }
  return createPortal(
    <div role="region" aria-label="Unsaved changes"
      className="fixed inset-x-3 bottom-3 z-40 flex justify-center pb-[env(safe-area-inset-bottom)] sm:bottom-5 lg:left-[calc(var(--shell-sidebar-w,0px)+12px)]">
      <div className="flex w-full max-w-[560px] animate-bar-in items-center gap-3 rounded-[14px] border border-border bg-surface-raised py-2 pr-2 pl-4 shadow-bar">
        <span className="relative flex size-2 shrink-0"><span className="absolute inset-0 animate-ping rounded-full bg-warning opacity-50" /><span className="relative size-2 rounded-full bg-warning" /></span>
        <span className="min-w-0 flex-1 truncate text-base font-medium text-text">{message}</span>
        <Button variant="ghost" onClick={() => void discard()} disabled={saving}>Discard</Button>
        <Button variant="primary" onClick={save} loading={saving} disabled={saveDisabled}>
          {saveLabel}<span className="ml-1 hidden sm:inline-flex"><Kbd combo="mod+s" tone="inverse" /></span>
        </Button>
      </div>
    </div>,
    document.body,
  )
}
